import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Divider,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Print as PrintIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import accountsApiService from '../../../services/accountsApiService';

const InvoiceView = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [invoiceData, setInvoiceData] = useState(null);
  const [error, setError] = useState(null);

  // Get parameters from URL
  const appId = searchParams.get('appId');
  const payType = searchParams.get('payType');
  const payid = searchParams.get('payid');

  useEffect(() => {
    if (appId && payType && payid) {
      loadInvoiceData();
    } else {
      setError('Missing required parameters');
      setLoading(false);
    }
  }, [appId, payType, payid]);

  const loadInvoiceData = async () => {
    try {
      setLoading(true);
      
      // First check assessment type for redirects (like C# logic)
      const assessmentType = await checkAssessmentType();
      if (assessmentType === 3) {
        // Redirect to desktop invoice
        window.location.href = `/main/accounts/invoice-desktop?appId=${appId}&payType=${payType}&payid=${payid}`;
        return;
      }
      if (assessmentType === 4 || assessmentType === 5 || assessmentType === 7) {
        // Redirect to 201 category invoice
        window.location.href = `/main/accounts/invoice-201?appId=${appId}&payType=${payType}&payid=${payid}`;
        return;
      }

      const response = await accountsApiService.getInvoiceDetails(appId, payType, payid);
      
      if (response.success && response.data) {
        setInvoiceData(response.data);
      } else {
        throw new Error(response.message || 'No data received');
      }
    } catch (error) {
      console.error('Error loading invoice data:', error);
      setError('Failed to load invoice data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Check assessment type (mimics C# logic)
  const checkAssessmentType = async () => {
    try {
      const response = await accountsApiService.getAssessmentType(appId);
      return response.assessmentType || 1; // Default to 1 if not found
    } catch (error) {
      console.error('Error checking assessment type:', error);
      return 1; // Default assessment type
    }
  };

  // Remove the generateMockInvoiceData function as we're now fully dynamic
  const generateMockInvoiceData = () => {
    // This function is no longer needed as we're using dynamic API data
    // Keeping it as fallback only
    const currentYear = new Date().getFullYear();
    const nextYear = currentYear + 1;
    
    return {
      invoiceNo: `NAPPL/100/${currentYear}-${nextYear}`,
      invoiceDate: new Date().toLocaleDateString('en-IN'),
      invoiceType: 'TAX INVOICE',
      labId: `M-${6400 + parseInt(appId || 1)}`,
      labName: 'SAMPLE DIAGNOSTICS LABORATORY',
      address: 'Sample Address for Testing',
      city: 'Sample City',
      gstin: '29AAQFT1379PIZ2',
      stateName: 'KARNATAKA',
      stateCode: '29',
      totalAmount: 22066,
      basicAmount: 18695,
      gstAmount: 3371,
      sgstAmount: 1685.5,
      cgstAmount: 1685.5,
      igstAmount: 3371,
      assessmentType: 'FIRST ACCREDITATION',
      applicationType: 'APPLICATION FEE',
      showGST: true,
      showTDS: false,
      isSameState: false,
      amountInWords: 'Rs. TWENTY TWO THOUSAND SIXTY SIX Only',
      gstAmountInWords: 'Rs. THREE THOUSAND THREE HUNDRED SEVENTY ONE Only',
      remarks: 'PLEASE REFER NABL 100 FOR PAYMENT MODES.',
      hsnsac: '998346',
      transactionDateTime: new Date().toISOString(),
    };
  };

  const numberToWords = (number) => {
    if (number === 0) return 'zero';
    if (number < 0) return 'minus ' + numberToWords(Math.abs(number));

    let words = '';
    const unitsMap = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
    const tensMap = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

    if (number >= 100000) {
      words += numberToWords(Math.floor(number / 100000)) + ' lakhs ';
      number %= 100000;
    }

    if (number >= 1000) {
      words += numberToWords(Math.floor(number / 1000)) + ' thousand ';
      number %= 1000;
    }

    if (number >= 100) {
      words += numberToWords(Math.floor(number / 100)) + ' hundred ';
      number %= 100;
    }

    if (number > 0) {
      if (words !== '') words += 'and ';

      if (number < 20) {
        words += unitsMap[number];
      } else {
        words += tensMap[Math.floor(number / 10)];
        if (number % 10 > 0) {
          words += '-' + unitsMap[number % 10];
        }
      }
    }

    return words.trim();
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = async () => {
    try {
      setLoading(true);
      
      try {
        // Try to get PDF data from backend
        const response = await accountsApiService.generateInvoicePDF(appId, payType, payid);
        
        if (response.success && response.generateClientSide && response.data) {
          // Backend provided data for client-side generation
          generateClientSidePDF(response.data);
          return;
        }
      } catch (apiError) {
        console.warn('Backend PDF generation failed, using current invoice data:', apiError);
      }
      
      // Fallback: Use current invoice data to generate PDF
      if (invoiceData) {
        generateClientSidePDF(invoiceData);
      } else {
        throw new Error('No invoice data available for PDF generation');
      }
      
    } catch (error) {
      console.error('Error downloading PDF:', error);
      
      // Final fallback: Use browser print functionality
      alert('PDF generation failed. The print dialog will open - please select "Save as PDF" to download the invoice.');
      handlePrint();
    } finally {
      setLoading(false);
    }
  };

  // Generate PDF using client-side method
  const generateClientSidePDF = (data) => {
    try {
      // Create a new window for PDF generation
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      
      if (!printWindow) {
        throw new Error('Pop-up blocked. Please allow pop-ups and try again.');
      }

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Invoice ${data.invoiceNo || 'Download'}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              font-size: 13px; 
              line-height: 1.4;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-bottom: 10px; 
            }
            td, th { 
              border: 1px solid black; 
              padding: 8px; 
              font-size: 13px; 
              vertical-align: top;
            }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .font-bold { font-weight: bold; }
            .no-print { display: none; }
            
            @media print {
              body { margin: 0; font-size: 12px; }
              .no-print { display: none !important; }
              table { page-break-inside: avoid; }
            }
            
            @page {
              margin: 0.5in;
              size: A4;
            }
          </style>
        </head>
        <body onload="window.print(); window.close();">
          ${generatePrintableInvoiceHTML(data)}
        </body>
        </html>
      `);
      
      printWindow.document.close();
      
    } catch (error) {
      console.error('Client-side PDF generation failed:', error);
      alert('PDF generation failed: ' + error.message);
    }
  };

  const handleBack = () => {
    navigate('/main/accounts/payment-details');
  };

  // Generate HTML content for PDF printing
  const generatePrintableInvoiceHTML = (data = invoiceData) => {
    if (!data) return '';
    
    return `
      <style>
        @media print {
          body { margin: 0; padding: 20px; font-family: Arial, sans-serif; font-size: 12px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
          th, td { border: 1px solid #000; padding: 8px; text-align: left; }
          .text-center { text-align: center !important; }
          .text-right { text-align: right !important; }
          .font-bold { font-weight: bold !important; }
          .no-border { border: none !important; }
          h3 { margin: 0; padding: 10px 0; }
        }
        body { margin: 0; padding: 20px; font-family: Arial, sans-serif; font-size: 12px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
        th, td { border: 1px solid #000; padding: 8px; text-align: left; }
        .text-center { text-align: center !important; }
        .text-right { text-align: right !important; }
        .font-bold { font-weight: bold !important; }
        .no-border { border: none !important; }
        h3 { margin: 0; padding: 10px 0; }
      </style>
      
      <div class="text-center" style="margin-bottom: 20px;">
        <h3>${data.invoiceType || (data.isGst ? 'TAX INVOICE' : 'BILL OF SUPPLY')} (Original for Recipient)</h3>
      </div>
      
      <table>
        <tr>
          <td style="width: 50%; vertical-align: top;">
            <strong>QUALITY COUNCIL OF INDIA<br/>
            BOARD : NABL<br/>
            PLOT NO.45, SECTOR 44<br/>
            GURUGRAM, HARYANA -122003<br/>
            Ph : 0124-4679700<br/>
            GSTIN : 06AAATQ0055D1Z2<br/>
            GSTIN/UIN : 06AAATQ0055D1Z2<br/>
            State Name : Haryana, Code : 06<br/>
            E-Mail: accounts@nabl.qcin.org</strong>
          </td>
          <td style="width: 50%; padding: 0;">
            <table style="width: 100%;">
              <tr>
                <td style="width: 50%;">Invoice No.<br/><strong>${data.invoiceNo || ''}</strong></td>
                <td style="width: 50%;">Dated<br/><strong>${data.invoiceDate || ''}</strong></td>
              </tr>
              <tr><td>Delivery Note<br/></td><td>Mode/Terms of Payment<br/></td></tr>
              <tr><td>Supplier's Ref.<br/></td><td>Other Reference(s)<br/></td></tr>
              <tr><td>Buyer's Order No.<br/></td><td>Dated<br/></td></tr>
              <tr><td>Despatch Document No.<br/></td><td>Dated<br/></td></tr>
              <tr><td>Despatched through<br/></td><td>Destination<br/></td></tr>
              <tr><td colspan="2">Terms of Delivery<br/></td></tr>
            </table>
          </td>
        </tr>
        <tr>
          <td style="vertical-align: top;">
            <strong>Buyer</strong><br/>
            LabId: <strong>${data.labId || ''}</strong><br/>
            <strong>${data.labName || ''}</strong><br/>
            <strong>${data.address || ''}</strong><br/>
            <strong>${data.city || ''}</strong><br/>
            GSTIN/UIN: <strong>${data.gstin || ''}</strong><br/>
            STATE NAME: <strong>${data.stateName?.toUpperCase() || ''}</strong>, State Code: <strong>${data.stateCode || ''}</strong><br/>
            Place of Supply: <strong>${data.stateName?.toUpperCase() || ''}</strong>
          </td>
        </tr>
      </table>
      
      <table>
        <thead>
          <tr>
            <th style="width: 5%;" class="text-center">SI. No</th>
            <th style="width: 45%;" class="text-center">Description of Goods</th>
            <th style="width: 10%;" class="text-center">HSN/SAC</th>
            <th style="width: 10%;" class="text-center">Rate</th>
            <th style="width: 15%;" class="text-center">per</th>
            <th style="width: 15%;" class="text-center">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td></td>
            <td class="font-bold">
              ${data.applicationType && parseInt(payType) !== 2 ? '<span class="font-bold">APPLICATION FEE</span><br/>' : ''}
              ${data.applicationType || 'APPLICATION FEE'}<br/>
              ${data.assessmentType || ''}
            </td>
            <td class="text-center">${data.hsnsac || '998346'}</td>
            <td></td>
            <td></td>
            <td class="text-center">₹${(data.basicAmount || 0).toLocaleString('en-IN')}</td>
          </tr>
          
          ${data.showTDS && data.tdsAmount > 0 ? `
          <tr>
            <td></td>
            <td class="text-right font-bold" style="padding-right: 20px;">Less TDS @ 10%</td>
            <td></td>
            <td class="text-center">10</td>
            <td class="text-center">%</td>
            <td class="text-center">₹${Math.round(data.tdsAmount).toLocaleString('en-IN')}</td>
          </tr>
          ` : ''}
          
          ${data.showGST && data.gstAmount > 0 ? (
            data.isSameState ? `
            <tr>
              <td></td>
              <td class="text-right font-bold" style="padding-right: 20px;">
                Add SGST @ 9%<br/>CGST @ 9%
              </td>
              <td></td>
              <td class="text-center">9<br/>9</td>
              <td class="text-center">%<br/>%</td>
              <td class="text-center">
                ₹${Math.round(data.sgstAmount || data.gstAmount / 2).toLocaleString('en-IN')}<br/>
                ₹${Math.round(data.cgstAmount || data.gstAmount / 2).toLocaleString('en-IN')}
              </td>
            </tr>
            ` : `
            <tr>
              <td></td>
              <td class="text-right font-bold" style="padding-right: 20px;">Add IGST @ 18%</td>
              <td></td>
              <td class="text-center">18</td>
              <td class="text-center">%</td>
              <td class="text-center">₹${Math.round(data.igstAmount || data.gstAmount).toLocaleString('en-IN')}</td>
            </tr>
            `
          ) : ''}
          
          <tr style="height: 60px;">
            <td></td><td></td><td></td><td></td><td></td><td></td>
          </tr>
          
          <tr>
            <td></td>
            <td class="text-right font-bold">Total</td>
            <td></td><td></td><td></td>
            <td class="text-center font-bold">₹${(data.totalAmount || 0).toLocaleString('en-IN')}</td>
          </tr>
          
          <tr>
            <td colspan="5">Amount Chargeable (in words)</td>
            <td class="text-right font-bold">E. & O.E</td>
          </tr>
          <tr>
            <td colspan="6" class="font-bold">
              ${data.amountInWords || `Rs. ${numberToWords(data.totalAmount || 0).toUpperCase()} Only`}
            </td>
          </tr>
        </tbody>
      </table>
      
      ${data.showGST && data.gstAmount > 0 ? `
      <table>
        <thead>
          <tr>
            <th style="width: 50%;" class="text-center">HSN/SAC</th>
            <th class="text-center">Taxable Value</th>
            <th colspan="2" class="text-center">${data.isSameState ? 'State/Central Tax' : 'Integrated Tax'}</th>
            <th class="text-center">Total Tax Amount</th>
          </tr>
          <tr>
            <th></th><th></th>
            <th style="width: 10%;" class="text-center">Rate</th>
            <th style="width: 10%;" class="text-center">Amount</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="text-center">${data.hsnsac || '998346'}</td>
            <td class="text-center">₹${(data.basicAmount || 0).toLocaleString('en-IN')}</td>
            <td class="text-center">18%</td>
            <td class="text-center">₹${Math.round(data.gstAmount || 0).toLocaleString('en-IN')}</td>
            <td class="text-center">₹${Math.round(data.gstAmount || 0).toLocaleString('en-IN')}</td>
          </tr>
          <tr>
            <td class="text-right font-bold">Total</td>
            <td class="text-center font-bold">₹${(data.basicAmount || 0).toLocaleString('en-IN')}</td>
            <td></td>
            <td class="text-center font-bold">₹${Math.round(data.gstAmount || 0).toLocaleString('en-IN')}</td>
            <td class="text-center font-bold">₹${Math.round(data.gstAmount || 0).toLocaleString('en-IN')}</td>
          </tr>
        </tbody>
      </table>
      
      <table>
        <tbody>
          <tr>
            <td colspan="6"><strong>Tax Amount (in words): </strong></td>
          </tr>
          <tr>
            <td colspan="6" class="font-bold">
              ${data.gstAmountInWords || `Rs. ${numberToWords(Math.round(data.gstAmount || 0)).toUpperCase()} Only`}
            </td>
          </tr>
        </tbody>
      </table>
      ` : ''}
      
      <table>
        <tbody>
          <tr>
            <td colspan="2" style="width: 50%;">Company's PAN : AAATQ0055D</td>
            <td colspan="4" style="width: 50%;" class="text-right font-bold">For Quality Council of India</td>
          </tr>
          <tr>
            <td colspan="2" style="width: 50%;">
              <strong>Declaration</strong><br/>
              ${data.remarks || (data.showGST ? 'PLEASE REFER NABL 100 FOR PAYMENT MODES.' : 'SUPPLY MEANT FOR EXPORT / SUPPLY TO SEZ UNIT FOR AUTHORISED OPERATIONS UNDER BOND OR LETTER OF UNDERTAKING WITHOUT PAYMENT OF INTEGRATED TAX.')}
            </td>
            <td colspan="4" style="width: 50%; height: 60px; vertical-align: bottom;" class="text-right font-bold">
              Authorised Signatory
            </td>
          </tr>
        </tbody>
      </table>
      
      <div class="text-center" style="margin-top: 20px;">
        <p>This is a Computer Generated Invoice</p>
      </div>
    `;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Payment Details
        </Button>
      </Box>
    );
  }

  if (!invoiceData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          No invoice data available
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: '210mm', mx: 'auto' }}>
      {/* Action Buttons */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, '@media print': { display: 'none' } }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back
        </Button>
        <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint}>
          Print
        </Button>
        <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
          Download PDF
        </Button>
      </Box>

      {/* Invoice Content */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          '@media print': { 
            boxShadow: 'none',
            margin: 0,
            padding: '10px'
          } 
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            {invoiceData.invoiceType || (invoiceData.isGst ? 'TAX INVOICE' : 'BILL OF SUPPLY')} (Original for Recipient)
          </Typography>
        </Box>

        {/* Main Invoice Table */}
        <TableContainer component={Box} sx={{ border: 1, borderColor: 'black' }}>
          <Table size="small" sx={{ '& .MuiTableCell-root': { border: 1, borderColor: 'black', fontSize: '13px' } }}>
            {/* Seller and Invoice Info Row */}
            <TableBody>
              <TableRow>
                <TableCell sx={{ width: '50%', p: 1, verticalAlign: 'top' }}>
                  <Typography variant="body2" fontWeight="bold">
                    QUALITY COUNCIL OF INDIA<br />
                    BOARD : NABL<br />
                    PLOT NO.45, SECTOR 44<br />
                    GURUGRAM, HARYANA -122003<br />
                    Ph : 0124-4679700<br />
                    GSTIN : 06AAATQ0055D1Z2<br />
                    GSTIN/UIN : 06AAATQ0055D1Z2<br />
                    State Name : Haryana, Code : 06<br />
                    E-Mail: accounts@nabl.qcin.org
                  </Typography>
                </TableCell>
                <TableCell sx={{ width: '50%', p: 0 }}>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { border: 1, borderColor: 'black', fontSize: '12px', p: 0.5 } }}>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ width: '50%' }}>
                          Invoice No.<br />
                          <Typography fontWeight="bold">{invoiceData.invoiceNo || ''}</Typography>
                        </TableCell>
                        <TableCell sx={{ width: '50%' }}>
                          Dated<br />
                          <Typography fontWeight="bold">{invoiceData.invoiceDate || ''}</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Delivery Note<br /></TableCell>
                        <TableCell>Mode/Terms of Payment<br /></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Supplier's Ref.<br /></TableCell>
                        <TableCell>Other Reference(s)<br /></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Buyer's Order No.<br /></TableCell>
                        <TableCell>Dated<br /></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Despatch Document No.<br /></TableCell>
                        <TableCell>Dated<br /></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Despatched through<br /></TableCell>
                        <TableCell>Destination<br /></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={2}>Terms of Delivery<br /></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableCell>
              </TableRow>
              
              {/* Buyer Info Row */}
              <TableRow>
                <TableCell sx={{ p: 1, verticalAlign: 'top' }}>
                  <Typography variant="body2">
                    <strong>Buyer</strong><br />
                    LabId: <strong>{invoiceData.labId || ''}</strong><br />
                    <strong>{invoiceData.labName || ''}</strong><br />
                    <strong>{invoiceData.address || ''}</strong><br />
                    <strong>{invoiceData.city || ''}</strong><br />
                    GSTIN/UIN: <strong>{invoiceData.gstin || ''}</strong><br />
                    STATE NAME: <strong>{invoiceData.stateName?.toUpperCase() || ''}</strong>, State Code: <strong>{invoiceData.stateCode || ''}</strong><br />
                    Place of Supply: <strong>{invoiceData.stateName?.toUpperCase() || ''}</strong>
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Items Table */}
        <TableContainer component={Box} sx={{ border: 1, borderColor: 'black', borderTop: 0 }}>
          <Table size="small" sx={{ '& .MuiTableCell-root': { border: 1, borderColor: 'black', fontSize: '13px', p: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '5%', textAlign: 'center' }}>SI. No</TableCell>
                <TableCell sx={{ width: '45%', textAlign: 'center' }}>Description of Goods</TableCell>
                <TableCell sx={{ width: '10%', textAlign: 'center' }}>HSN/SAC</TableCell>
                <TableCell sx={{ width: '10%', textAlign: 'center' }}>Rate</TableCell>
                <TableCell sx={{ width: '15%', textAlign: 'center' }}>per</TableCell>
                <TableCell sx={{ width: '15%', textAlign: 'center' }}>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Main item */}
              <TableRow>
                <TableCell></TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  {invoiceData.applicationType && parseInt(payType) !== 2 && (
                    <>
                      <span style={{ fontWeight: 'bold' }}>APPLICATION FEE</span><br />
                    </>
                  )}
                  {invoiceData.applicationType || 'APPLICATION FEE'}<br />
                  {invoiceData.assessmentType || ''}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>{invoiceData.hsnsac || '998346'}</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell sx={{ textAlign: 'center' }}>₹{(invoiceData.basicAmount || 0).toLocaleString('en-IN')}</TableCell>
              </TableRow>

              {/* TDS Row (if applicable) */}
              {invoiceData.showTDS && invoiceData.tdsAmount > 0 && (
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', pr: 4 }}>
                    Less TDS @ 10%
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>10</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>%</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    ₹{Math.round(invoiceData.tdsAmount).toLocaleString('en-IN')}
                  </TableCell>
                </TableRow>
              )}

              {/* GST Rows */}
              {invoiceData.showGST && invoiceData.gstAmount > 0 && (
                <>
                  {invoiceData.isSameState ? (
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', pr: 4 }}>
                        Add SGST @ 9%<br />
                        CGST @ 9%
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>9<br />9</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>%<br />%</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        ₹{Math.round(invoiceData.sgstAmount || invoiceData.gstAmount / 2).toLocaleString('en-IN')}<br />
                        ₹{Math.round(invoiceData.cgstAmount || invoiceData.gstAmount / 2).toLocaleString('en-IN')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', pr: 4 }}>
                        Add IGST @ 18%
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>18</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>%</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        ₹{Math.round(invoiceData.igstAmount || invoiceData.gstAmount).toLocaleString('en-IN')}
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}

              {/* Empty rows for spacing */}
              <TableRow sx={{ height: '100px' }}>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>

              {/* Total Row */}
              <TableRow>
                <TableCell></TableCell>
                <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>Total</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                  ₹{(invoiceData.totalAmount || 0).toLocaleString('en-IN')}
                </TableCell>
              </TableRow>

              {/* Amount in words */}
              <TableRow>
                <TableCell colSpan={5}>Amount Chargeable (in words)</TableCell>
                <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>E. & O.E</TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={6} sx={{ fontWeight: 'bold' }}>
                  {invoiceData.amountInWords || `Rs. ${numberToWords(invoiceData.totalAmount || 0).toUpperCase()} Only`}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* GST Summary Table */}
        {invoiceData.showGST && invoiceData.gstAmount > 0 && (
          <TableContainer component={Box} sx={{ border: 1, borderColor: 'black', borderTop: 0 }}>
            <Table size="small" sx={{ '& .MuiTableCell-root': { border: 1, borderColor: 'black', fontSize: '13px', p: 1 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '50%', textAlign: 'center' }}>HSN/SAC</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>Taxable Value</TableCell>
                  <TableCell colSpan={2} sx={{ textAlign: 'center' }}>
                    {invoiceData.isSameState ? 'State/Central Tax' : 'Integrated Tax'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>Total Tax Amount</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell sx={{ width: '10%', textAlign: 'center' }}>Rate</TableCell>
                  <TableCell sx={{ width: '10%', textAlign: 'center' }}>Amount</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ textAlign: 'center' }}>{invoiceData.hsnsac || '998346'}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>₹{(invoiceData.basicAmount || 0).toLocaleString('en-IN')}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>18%</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>₹{Math.round(invoiceData.gstAmount || 0).toLocaleString('en-IN')}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>₹{Math.round(invoiceData.gstAmount || 0).toLocaleString('en-IN')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>Total</TableCell>
                  <TableCell sx={{ textAlign: 'center', fontWeight: 'bold' }}>₹{(invoiceData.basicAmount || 0).toLocaleString('en-IN')}</TableCell>
                  <TableCell></TableCell>
                  <TableCell sx={{ textAlign: 'center', fontWeight: 'bold' }}>₹{Math.round(invoiceData.gstAmount || 0).toLocaleString('en-IN')}</TableCell>
                  <TableCell sx={{ textAlign: 'center', fontWeight: 'bold' }}>₹{Math.round(invoiceData.gstAmount || 0).toLocaleString('en-IN')}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Tax amount in words */}
        {invoiceData.showGST && invoiceData.gstAmount > 0 && (
          <TableContainer component={Box} sx={{ border: 1, borderColor: 'black', borderTop: 0 }}>
            <Table size="small" sx={{ '& .MuiTableCell-root': { border: 1, borderColor: 'black', fontSize: '13px', p: 1 } }}>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={6}>
                    <strong>Tax Amount (in words): </strong>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={6} sx={{ fontWeight: 'bold' }}>
                    {invoiceData.gstAmountInWords || `Rs. ${numberToWords(Math.round(invoiceData.gstAmount || 0)).toUpperCase()} Only`}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Footer */}
        <TableContainer component={Box} sx={{ border: 1, borderColor: 'black', borderTop: 0 }}>
          <Table size="small" sx={{ '& .MuiTableCell-root': { border: 1, borderColor: 'black', fontSize: '13px', p: 1 } }}>
            <TableBody>
              <TableRow>
                <TableCell colSpan={2} sx={{ width: '50%' }}>
                  Company's PAN : AAATQ0055D
                </TableCell>
                <TableCell colSpan={4} sx={{ width: '50%', textAlign: 'right', fontWeight: 'bold' }}>
                  For Quality Council of India
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={2} sx={{ width: '50%' }}>
                  <strong>Declaration</strong><br />
                  {invoiceData.remarks || (invoiceData.showGST ? 'PLEASE REFER NABL 100 FOR PAYMENT MODES.' : 'SUPPLY MEANT FOR EXPORT / SUPPLY TO SEZ UNIT FOR AUTHORISED OPERATIONS UNDER BOND OR LETTER OF UNDERTAKING WITHOUT PAYMENT OF INTEGRATED TAX.')}
                </TableCell>
                <TableCell colSpan={4} sx={{ width: '50%', textAlign: 'right', fontWeight: 'bold', height: '60px', verticalAlign: 'bottom' }}>
                  Authorised Signatory
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Computer generated note */}
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="body2">
            This is a Computer Generated Invoice
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default InvoiceView;